import { useLazyQuery, useMutation } from '@apollo/client'
import { Autocomplete, CircularProgress, createFilterOptions, TextField } from '@mui/material'
import debounce from 'lodash/debounce'
import React, { forwardRef, useEffect, useRef, useState } from 'react'

import { CREATE_LOOKUP_TABLE_MUTATION } from '../../../graphql/mutations/createLookupTableMutation'
import { LOOKUP_TABLE_QUERY } from '../../../graphql/queries/lookupTableQuery'

const LOOKUP_TYPE = 'external_rsv'

interface Option {
  id?: string
  value: string
  inputValue?: string
}

const filter = createFilterOptions<Option>()

export interface IExternalRsv {
  value?: string
  label?: string
  onChange?: (value: string) => void
  disabled?: boolean
  error?: any
  helperText?: string
}

const ExternalRsv = forwardRef(
  (
    { value, label, onChange, disabled, error, helperText }: IExternalRsv,
    ref
  ): React.JSX.Element => {
    const [inputValue, setInputValue] = useState(value || '')
    const [options, setOptions] = useState<Option[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
      setInputValue(value || '')
    }, [value])

    const [executeSearch, { data: searchData, error: searchError }] = useLazyQuery(
      LOOKUP_TABLE_QUERY,
      { fetchPolicy: 'network-only' }
    )

    useEffect(() => {
      if (searchData) {
        const collection = searchData?.lookupTables?.collection ?? []
        setOptions(collection.map((item: any) => ({ id: item.id, value: item.value })))
        setLoading(false)
      }
    }, [searchData])

    useEffect(() => {
      if (searchError) setLoading(false)
    }, [searchError])

    const [createLookupTable] = useMutation(CREATE_LOOKUP_TABLE_MUTATION)

    const debouncedSearch = useRef(
      debounce((text: string) => {
        setLoading(true)
        executeSearch({
          variables: {
            filter: { lookupType: LOOKUP_TYPE, textSearch: text || undefined }
          }
        })
      }, 300)
    ).current

    useEffect(() => {
      if (!disabled) debouncedSearch(inputValue)
      return () => debouncedSearch.cancel()
    }, [inputValue, debouncedSearch, disabled])

    const handleChange = async (_event: React.SyntheticEvent, newValue: Option | string | null) => {
      if (!newValue) {
        onChange?.('')
        setInputValue('')
        return
      }

      if (typeof newValue === 'string') {
        onChange?.(newValue)
        setInputValue(newValue)
        return
      }

      if (newValue.inputValue) {
        try {
          const result = await createLookupTable({
            variables: {
              attributes: { lookupType: LOOKUP_TYPE, value: newValue.inputValue }
            }
          })
          const created = result.data?.createLookupTable
          if (created) {
            onChange?.(created.value)
            setInputValue(created.value)
          }
        } catch (_err) {
          // creation failed — fall back to raw text
          onChange?.(newValue.inputValue)
          setInputValue(newValue.inputValue)
        }
        return
      }

      onChange?.(newValue.value)
      setInputValue(newValue.value)
    }

    return (
      <Autocomplete<Option, false, false, true>
        disabled={disabled}
        freeSolo
        size="small"
        loading={loading}
        value={inputValue}
        options={options}
        getOptionLabel={option =>
          typeof option === 'string' ? option : (option.inputValue ?? option.value)
        }
        filterOptions={(currentOptions, params) => {
          const filtered = filter(currentOptions, params)
          const { inputValue: input } = params
          const exists = currentOptions.some(opt => opt.value.toLowerCase() === input.toLowerCase())
          if (input.trim() && !exists) {
            filtered.push({ inputValue: input, value: `Add "${input}"` })
          }
          return filtered
        }}
        onInputChange={(_event, newInput, reason) => {
          if (reason === 'input') {
            setInputValue(newInput)
            onChange?.(newInput)
          }
        }}
        onChange={handleChange}
        renderOption={(props, option) => (
          <li {...props} key={option.id ?? option.inputValue ?? option.value}>
            {option.value}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            inputRef={ref}
            error={!!error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    )
  }
)

ExternalRsv.displayName = 'ExternalRsv'

export default ExternalRsv
